import React, { useEffect, useMemo, useRef, useState } from 'react'
import { fetchText, parseSpec } from '../utils/spec'

type RedocExProps = {
  specUrl: string
  style?: React.CSSProperties
  dark?: boolean // default: true
  debug?: boolean // default: false
}

// Note: We intentionally avoid transforming tags or injecting x-tagGroups.
// Specs are expected to define `x-tagGroups` themselves. This keeps the
// rendered navigation exactly as authored in the spec.

function cleanTagName(name: string): string {
  // Remove all occurrences of [ ... ] and collapse whitespace
  return name.replace(/\[[^\]]*]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Apply display-only normalization for tag labels by setting `x-displayName`
 * on top-level `spec.tags[]` entries. This preserves the original tag names
 * everywhere (including `operation.tags` and `x-tagGroups[].tags`) so grouping
 * and identity remain intact, while Redoc shows cleaned labels in the UI.
 *
 * Also ensures that any tag names discovered in operations but missing from
 * `spec.tags` are appended (in order) with an appropriate `x-displayName`.
 */
function applyDisplayNamesForTags(spec: any, debug = false) {
  if (!spec || typeof spec !== 'object') return spec

  // Build ordered list of all tag names: first from spec.tags in authored order,
  // then from operations in first-seen order.
  const allNamesSet = new Set<string>()
  const allNamesList: string[] = []

  const topTagsArray = Array.isArray(spec.tags) ? spec.tags : []
  for (const t of topTagsArray) {
    if (!t || typeof t.name !== 'string') continue
    const name = String(t.name)
    if (!allNamesSet.has(name)) {
      allNamesSet.add(name)
      allNamesList.push(name)
    }
  }

  const paths = spec.paths || {}
  for (const p of Object.keys(paths)) {
    const item = paths[p]
    if (!item || typeof item !== 'object') continue
    for (const method of Object.keys(item)) {
      const op = item[method]
      if (!op || !Array.isArray(op.tags)) continue
      for (const tag of op.tags) {
        if (typeof tag !== 'string') continue
        const name = tag
        if (!allNamesSet.has(name)) {
          allNamesSet.add(name)
          allNamesList.push(name)
        }
      }
    }
  }

  // Rebuild spec.tags by preserving the first tag object per unique name
  // and adding any missing names at the end. Assign x-displayName for UI.
  const seenTop = new Set<string>()
  const rebuilt: Array<Record<string, any>> = []
  for (const t of topTagsArray) {
    if (!t || typeof t.name !== 'string') continue
    const name = String(t.name)
    if (seenTop.has(name)) continue
    seenTop.add(name)
    const cleaned = cleanTagName(name)
    const tagObj: Record<string, any> = { ...t }
    if (cleaned && cleaned !== name) {
      tagObj['x-displayName'] = cleaned
    } else if (!cleaned) {
      // Edge case: name cleaned to empty string; keep original without display override
    }
    rebuilt.push(tagObj)
  }

  // Append missing names (from operations) while preserving order
  for (const name of allNamesList) {
    if (!seenTop.has(name)) {
      seenTop.add(name)
      const cleaned = cleanTagName(name)
      const tagObj: Record<string, any> = { name }
      if (cleaned && cleaned !== name) tagObj['x-displayName'] = cleaned
      rebuilt.push(tagObj)
    }
  }

  spec.tags = rebuilt

  if (debug) {
    // eslint-disable-next-line no-console
    console.debug('[RedocEx] Applied x-displayName for tags; original names preserved for grouping')
  }

  return spec
}

/**
 * Reconcile authored x-tagGroups tag names with actual tag names used in operations.
 *
 * Motivation: Authors may list cleaned labels (e.g., "Template Scripts") inside
 * x-tagGroups, while operations actually use bracketed prefixes (e.g.,
 * "[Integrations] Template Scripts" or "[Developer] Template Scripts").
 * Redoc groups by exact tag name, so a mismatch yields empty groups and causes
 * our General fallback to pick those tags up incorrectly.
 *
 * Strategy: For each group tag name `T` in x-tagGroups:
 * - If `T` exactly exists among collected spec tag names, keep it.
 * - Else, find candidates among real tag names whose cleaned label equals `T`.
 *   - If exactly one candidate exists, replace `T` with that candidate.
 *   - If multiple candidates exist, prefer one whose original includes a
 *     bracketed segment matching the group name (e.g., "[Integrations]") or
 *     contains the group name text (case-insensitive). If still ambiguous, keep `T`.
 *
 * This is non-destructive and order-preserving for groups; it only substitutes
 * tag tokens so groups correctly reference actual tag keys used by operations.
 */
function reconcileXTagGroupsWithActualTags(spec: any, debug = false) {
  if (!spec || typeof spec !== 'object') return spec
  const groups = Array.isArray((spec as any)['x-tagGroups']) ? (spec as any)['x-tagGroups'] : null
  if (!groups) return spec

  // Collect actual tag names from spec.tags and operations
  const actualTagNamesSet = new Set<string>()
  const actualTagNamesList: string[] = []
  const topTagsArray = Array.isArray(spec.tags) ? spec.tags : []
  for (const t of topTagsArray) {
    if (t && typeof t.name === 'string') {
      const n = t.name
      if (!actualTagNamesSet.has(n)) {
        actualTagNamesSet.add(n)
        actualTagNamesList.push(n)
      }
    }
  }
  const paths = spec.paths || {}
  for (const p of Object.keys(paths)) {
    const item = paths[p]
    if (!item || typeof item !== 'object') continue
    for (const method of Object.keys(item)) {
      const op = item[method]
      if (!op || !Array.isArray(op.tags)) continue
      for (const tag of op.tags) {
        if (typeof tag !== 'string') continue
        const n = tag
        if (!actualTagNamesSet.has(n)) {
          actualTagNamesSet.add(n)
          actualTagNamesList.push(n)
        }
      }
    }
  }

  const actualByClean = new Map<string, string[]>()
  for (const n of actualTagNamesList) {
    const cleaned = cleanTagName(n)
    const arr = actualByClean.get(cleaned) || []
    arr.push(n)
    actualByClean.set(cleaned, arr)
  }

  let changed = false
  for (const grp of groups) {
    if (!grp || !Array.isArray(grp.tags)) continue
    const groupName: string = typeof grp.name === 'string' ? grp.name : ''
    const preferredBracket = groupName ? `[${groupName}]` : ''
    const lowerGroup = groupName.toLowerCase()
    const newTags: string[] = []
    for (const t of grp.tags) {
      if (actualTagNamesSet.has(t)) {
        newTags.push(t)
        continue
      }
      const cleaned = cleanTagName(t)
      const candidates = actualByClean.get(cleaned) || []
      if (candidates.length === 0) {
        // No match found; keep as-is
        newTags.push(t)
        continue
      }
      let pick: string | null = null
      if (candidates.length === 1) {
        pick = candidates[0]
      } else {
        // Prefer one that contains [GroupName]
        if (preferredBracket) {
          pick = candidates.find((c) => c.includes(preferredBracket)) || null
        }
        // Otherwise, prefer one that contains group name text
        if (!pick && lowerGroup) {
          pick = candidates.find((c) => c.toLowerCase().includes(lowerGroup)) || null
        }
        // Fallback to the first candidate
        if (!pick) pick = candidates[0]
      }
      if (pick) {
        if (pick !== t) changed = true
        newTags.push(pick)
      } else {
        newTags.push(t)
      }
    }
    grp.tags = newTags
  }

  if (changed && debug) {
    // eslint-disable-next-line no-console
    console.debug('[RedocEx] Reconciled x-tagGroups with actual tags based on cleaned-name matching')
  }

  return spec
}

/**
 * Ensure every tag is a member of some x-tagGroup by appending a fallback
 * group named `generalName` that contains any tags not present in existing groups.
 * If the spec has no x-tagGroups at all, one will be created with a single
 * `generalName` group containing all discovered tags. This is a non-destructive
 * additive transform: it never removes or reassigns tags that are already grouped.
 */
function addGeneralTagGroupIfMissing(spec: any, generalName = 'General', debug = false) {
  if (!spec || typeof spec !== 'object') return spec

  // 1) Collect all tag names used in the spec (from operations + top-level tags)
  // Preserve the order as authored in the YAML: first the order in spec.tags,
  // then any additional tags discovered in the order of first appearance in operations.
  const allTagNames = new Set<string>()
  const allTagList: string[] = []
  const topTags = Array.isArray(spec.tags) ? spec.tags : []
  for (const t of topTags) {
    if (t && typeof t.name === 'string') {
      const name = t.name.trim()
      if (name && !allTagNames.has(name)) {
        allTagNames.add(name)
        allTagList.push(name)
      }
    }
  }
  const paths = spec.paths || {}
  for (const p of Object.keys(paths)) {
    const item = paths[p]
    if (!item || typeof item !== 'object') continue
    for (const method of Object.keys(item)) {
      const op = item[method]
      if (!op || !Array.isArray(op.tags)) continue
      for (const tag of op.tags) {
        if (typeof tag === 'string') {
          const name = tag.trim()
          if (name && !allTagNames.has(name)) {
            allTagNames.add(name)
            allTagList.push(name)
          }
        }
      }
    }
  }

  // If no tags at all, nothing to do
  if (allTagList.length === 0) return spec

  // 2) Determine which tags are already in groups
  const xTagGroups = Array.isArray((spec as any)['x-tagGroups']) ? (spec as any)['x-tagGroups'] : null
  const grouped = new Set<string>()

  // 3) Compute ungrouped tags
  const ungrouped: string[] = []
  for (const name of allTagNames) {
    if (!grouped.has(name)) ungrouped.push(name)
  }

  if (ungrouped.length === 0 && xTagGroups) {
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[RedocEx] All tags are already assigned to x-tagGroups; no General group needed.')
    }
    return spec
  }

  // 4) Ensure top-level tags include all names so the renderer has metadata entries
  const ensuredTopTags: Array<{ name: string; description?: string }> = Array.isArray(spec.tags) ? [...spec.tags] : []
  const existing = new Set(ensuredTopTags.map((t) => t.name))
  for (const name of allTagList) {
    if (!existing.has(name)) {
      existing.add(name)
      ensuredTopTags.push({ name })
    }
  }
  spec.tags = ensuredTopTags

  // 5) Create/append the General group and make sure it's at the top
  const outGroups = xTagGroups ? [...xTagGroups] : []
  // If no groups exist, General should include all tags; otherwise only the ungrouped ones
  // Preserve authored order: use ungrouped in the order of allTagList, or allTagList itself when no groups.
  const generalTags = xTagGroups ? ungrouped : [...allTagList]

  // Find an existing General group if authored
  const existingGeneralIdx = outGroups.findIndex((g: any) => (g?.name || '').trim() === generalName)
  if (existingGeneralIdx >= 0) {
    // Merge any missing tags into the existing General group, preserving its current order
    const existingTags: string[] = Array.isArray(outGroups[existingGeneralIdx]?.tags)
      ? outGroups[existingGeneralIdx].tags.filter((t: any) => typeof t === 'string')
      : []
    const existingSet = new Set(existingTags)
    for (const t of generalTags) {
      if (!existingSet.has(t)) {
        existingSet.add(t)
        existingTags.push(t)
      }
    }
    outGroups[existingGeneralIdx] = { ...outGroups[existingGeneralIdx], tags: existingTags }
    // Move General to the top
    const [gen] = outGroups.splice(existingGeneralIdx, 1)
    outGroups.unshift(gen)
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[RedocEx] Ensured General group is first; merged tags count:', existingTags.length)
    }
  } else if (generalTags.length > 0) {
    // Insert a new General group at the top
    outGroups.unshift({ name: generalName, tags: generalTags })
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug('[RedocEx] Inserted General group at top with tags:', generalTags)
    }
  }

  ;(spec as any)['x-tagGroups'] = outGroups

  return spec
}

function loadRedocScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).Redoc) return resolve()
    const script = document.createElement('script')
    script.src = 'https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Redoc script'))
    document.head.appendChild(script)
  })
}

export default function RedocEx({
                                  specUrl,
                                  style,
                                  dark = true,
                                  debug = false,
                                }: RedocExProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Read current CSS variable values so Redoc matches our theme tokens
  const cssVars = useMemo(() => {
    try {
      const css = getComputedStyle(document.documentElement)
      const read = (name: string, fallback?: string) => (css.getPropertyValue(name).trim() || fallback || '').trim()
      const panel = read('--panel', dark ? '#1e1e1e' : '#ffffff')
      const panel2 = read('--panel-2', dark ? '#232323' : '#f8fbf2')
      const text = read('--text', dark ? 'rgba(255,255,255,0.87)' : '#000000')
      const textMuted = read('--text-muted', dark ? 'rgba(255,255,255,0.6)' : '#545454')
      const border = read('--border', dark ? '#2a2a2a' : '#e4e4e4')
      const brand = read('--brand', '#2a5932')
      const codeBg = read('--code-bg', dark ? '#0f0f0f' : '#f2f6ea')
      return { panel, panel2, text, textMuted, border, brand, codeBg }
    } catch {
      return {
        panel: dark ? '#1e1e1e' : '#ffffff',
        panel2: dark ? '#232323' : '#f8fbf2',
        text: dark ? 'rgba(255,255,255,0.87)' : '#000000',
        textMuted: dark ? 'rgba(255,255,255,0.6)' : '#545454',
        border: dark ? '#2a2a2a' : '#e4e4e4',
        brand: '#2a5932',
        codeBg: dark ? '#0f0f0f' : '#f2f6ea',
      }
    }
  }, [dark])

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({
      // Let the Redoc content determine its height so the page scrolls naturally
      height: 'auto',
      width: '100%',
      border: `1px solid ${cssVars.border}`,
      borderRadius: 12,
      // Avoid inner scroll boxes; use the page scroll instead
      overflow: 'visible',
      background: cssVars.panel,
      ...(style || {}),
    }),
    [style, cssVars.border, cssVars.panel]
  )

  useEffect(() => {
    let canceled = false

    async function run() {
      setError(null)
      setLoading(true)
      try {
        await loadRedocScript()
        const text = await fetchText(specUrl)
        const parsed = await parseSpec(text)
        // Apply display-only cleaning via x-displayName on spec.tags (do not mutate tag keys)
        const withDisplay = applyDisplayNamesForTags(parsed, debug)
        // Reconcile authored x-tagGroups tag entries with actual operation tag names
        const reconciled = reconcileXTagGroupsWithActualTags(withDisplay, debug)
        // Then, ensure a General group exists (and is first) for any ungrouped tags
        const finalSpec = addGeneralTagGroupIfMissing(reconciled, 'General', debug)
        if (debug) {
          const hasGroups = Boolean((finalSpec as any)['x-tagGroups'])
          // eslint-disable-next-line no-console
          console.debug('[RedocEx] Ensured General group fallback. x-tagGroups present:', hasGroups)
        }
        if (canceled) return
        const Redoc = (window as any).Redoc
        const options = {
          theme: dark
            ? {
              colors: {
                primary: { main: cssVars.brand },
                text: { primary: cssVars.text, secondary: cssVars.textMuted },
                border: { dark: cssVars.border },
              },
              sidebar: {
                backgroundColor: cssVars.panel,
                textColor: cssVars.textMuted,
              },
              typography: {
                fontSize: '14px',
                fontFamily:
                  'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
              },
              rightPanel: { backgroundColor: cssVars.panel },
              table: { backgroundColor: cssVars.panel2 },
              codeSample: {
                backgroundColor: cssVars.codeBg,
                textColor: cssVars.text,
              },
            }
            : undefined,
          hideDownloadButton: true,
          expandResponses: '200,201',
          jsonSampleExpandLevel: 2,
          pathInMiddlePanel: true,
        }
        if (containerRef.current) {
          // Clear previous content for hot reloads / route switches
          containerRef.current.innerHTML = ''
          await Redoc.init(finalSpec, options, containerRef.current)
        }
      } catch (e: any) {
        if (!canceled) setError(e?.message || 'Failed to render Redoc')
      } finally {
        if (!canceled) setLoading(false)
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    run()

    return () => {
      canceled = true
    }
  }, [specUrl, dark, debug])

  if (error) {
    return (
      <div className="card" role="alert">
        Failed to load API docs: {error}
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      {/* Scoped overrides to soften bright controls & improve contrast inside Redoc */}
      <style>
        {`
        /* Scope everything under Redoc root to avoid leaking */
        .redoc-wrap {
          --rd-bg: var(--bg);
          --rd-panel: var(--panel);
          --rd-panel-2: var(--panel-2);
          --rd-text: var(--text);
          --rd-text-muted: var(--text-muted);
          --rd-border: var(--border);
          --rd-code-bg: var(--code-bg);
          --rd-code-border: var(--code-border);
        }
        /* Sidebar menu: fix active item turning near-black and improve hover/active contrast */
        .redoc-wrap .menu-content .label,
        .redoc-wrap .menu-content .operation-link,
        .redoc-wrap .menu-content .section-title {
          color: var(--rd-text-muted);
        }
        .redoc-wrap .menu-content li:hover > .operation-link,
        .redoc-wrap .menu-content li:hover > .label,
        .redoc-wrap .menu-content .operation-link:hover {
          background: rgba(123,176,255,0.08) !important;
          color: var(--rd-text) !important;
        }
        /* Try all known active classes used by Redoc versions */
        .redoc-wrap .menu-content .active,
        .redoc-wrap .menu-content li.active > .operation-link,
        .redoc-wrap .menu-content .operation-link--active,
        .redoc-wrap .menu-content .operation-name-tag--active,
        .redoc-wrap .menu-content .tag-name--active {
          background: rgba(123,176,255,0.14) !important;
          color: var(--rd-text) !important;
        }
        /* Left sidebar block backgrounds should never be pitch black */
        .redoc-wrap .menu-content,
        .redoc-wrap .menu-content .scrollbar-container {
          background: var(--rd-panel) !important;
        }
        /* Inputs / dropdowns / selects (Servers, etc.) */
        .redoc-wrap input,
        .redoc-wrap select,
        .redoc-wrap textarea,
        .redoc-wrap .dropdown-menu,
        .redoc-wrap .menu-content .search-input {
          background: var(--rd-panel) !important;
          color: var(--rd-text) !important;
          border: 1px solid var(--rd-border) !important;
        }
        .redoc-wrap option { background: var(--rd-panel); color: var(--rd-text); }
        .redoc-wrap .dropdown-menu li:hover,
        .redoc-wrap .dropdown-menu [role="option"][aria-selected="true"],
        .redoc-wrap select:focus,
        .redoc-wrap input:focus {
          outline: none !important;
          box-shadow: 0 0 0 2px rgba(123,176,255,0.25) !important;
          border-color: var(--rd-border) !important;
        }

        /* Response samples/code blocks on right panel */
        .redoc-wrap pre,
        .redoc-wrap code,
        .redoc-wrap .code,
        .redoc-wrap .example-panel,
        .redoc-wrap .response .example,
        .redoc-wrap .right-panel .language-example,
        .redoc-wrap .json-schema-view__example {
          background: var(--rd-code-bg) !important;
          color: var(--rd-text) !important;
          border: 1px solid var(--rd-code-border) !important;
        }

        /* Align the container of Response samples to the same tone as the left menu */
        .redoc-wrap .right-panel,
        .redoc-wrap .right-panel .scrollbar-container,
        .redoc-wrap .response .response-panel,
        .redoc-wrap .response .examples-wrapper,
        .redoc-wrap .response .example-entity {
          background: var(--rd-panel) !important;
        }

        /* Section headers like PATH PARAMETERS, QUERY PARAMETERS */
        .redoc-wrap .section-title,
        .redoc-wrap .parameters h5,
        .redoc-wrap .parameters .param-title,
        .redoc-wrap .api-content h5,
        .redoc-wrap .api-content .section-header {
          color: var(--rd-text) !important;
          opacity: 0.95 !important;
        }

        /* Media type label near "Response Schema:" (e.g., application/json) */
        .redoc-wrap .response h5 .mime-type,
        .redoc-wrap .response h5 .content-type,
        .redoc-wrap .response h5 .response-type,
        .redoc-wrap .response h5 .response-content-type,
        .redoc-wrap .response .mime-type,
        .redoc-wrap .response .content-type,
        .redoc-wrap .response .response-type,
        .redoc-wrap .response .response-content-type,
        /* Fallback when rendered as inline code */
        .redoc-wrap .response h5 code {
          color: var(--rd-text) !important;
          background: rgba(255, 255, 255, 0.06) !important;
          border: 1px solid var(--rd-code-border) !important;
          border-radius: 6px !important;
          padding: 2px 6px !important;
        }

        /* Improve visibility of disclosure/caret arrows (e.g., next to AUTHORIZATIONS) */
        .redoc-wrap .section-title svg,
        .redoc-wrap .section-title svg path,
        .redoc-wrap .collapser svg,
        .redoc-wrap .collapser svg path,
        .redoc-wrap .operation-content > svg,
        .redoc-wrap .operation-content > svg path,
        .redoc-wrap .menu-content svg,
        .redoc-wrap .menu-content svg path {
          color: var(--rd-text-muted) !important;
          fill: var(--rd-text-muted) !important;
          stroke: var(--rd-text-muted) !important;
        }
        .redoc-wrap .section-title:hover svg,
        .redoc-wrap .section-title:hover svg path,
        .redoc-wrap .collapsible:is([open]) .section-title svg,
        .redoc-wrap .collapsible:is([open]) .section-title svg path,
        .redoc-wrap .menu-content li[aria-expanded="true"] > .label svg,
        .redoc-wrap .menu-content li[aria-expanded="true"] > .label svg path {
          color: var(--rd-text) !important;
          fill: var(--rd-text) !important;
          stroke: var(--rd-text) !important;
        }

        /* AUTHORIZATIONS box (security details) – make background darker and text readable */
        .redoc-wrap .security-definitions,
        .redoc-wrap .security-requirements,
        .redoc-wrap .authorization,
        .redoc-wrap .authorization__box,
        .redoc-wrap .security-scheme,
        .redoc-wrap .auth-types {
          background: var(--rd-panel) !important;
          color: var(--rd-text) !important;
          border: 1px solid var(--rd-border) !important;
        }
        .redoc-wrap .authorization__box input,
        .redoc-wrap .authorization__box select,
        .redoc-wrap .authorization__box textarea {
          background: var(--rd-panel-2) !important;
          color: var(--rd-text) !important;
          border: 1px solid var(--rd-border) !important;
        }
        /* Additional auth-related containers that may appear depending on version */
        .redoc-wrap .api-authorization,
        .redoc-wrap .authorization__desc,
        .redoc-wrap .security-list,
        .redoc-wrap .scopes__section,
        .redoc-wrap .scopes__box {
          background: var(--rd-panel) !important;
          color: var(--rd-text) !important;
          border: 1px solid var(--rd-border) !important;
        }
        .redoc-wrap .authorization code,
        .redoc-wrap .security-requirements code {
          background: var(--rd-code-bg) !important;
          border: 1px solid var(--rd-code-border) !important;
          color: var(--rd-text) !important;
        }
        `}
      </style>
      {loading ? (
        <div style={{ padding: 16, color: dark ? '#cbd5e1' : undefined }}>Loading API reference…</div>
      ) : null}
      <div ref={containerRef} style={{ width: '100%' }}/>
    </div>
  )
}
